import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldCheck, ShoppingBasket, CalendarClock, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import PlanGate from "./PlanGate";

async function sha256(value) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function loadRazorpay() {
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Plan() {
  const { data, refetch } = useQuery({ queryKey: ["my-plan"], queryFn: async () => (await api.get("/agent/plan")).data });
  const [busy, setBusy] = useState(false);

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!data.plan) return <PlanGate onActivated={() => refetch()} />;

  const renew = async () => {
    setBusy(true);
    try {
      const { data: co } = await api.post("/agent/plan/checkout", { type: "renewal" });
      if (co.mode === "mock") {
        const paymentId = `pay_mock_${crypto.randomUUID().slice(0, 8)}`;
        const signature = await sha256(`${co.order_id}|${paymentId}`);
        await api.post("/agent/plan/verify", { purchase_id: co.purchase_id, razorpay_order_id: co.order_id, razorpay_payment_id: paymentId, razorpay_signature: signature });
        toast.success("Annual service renewed for 1 year (test payment)");
        refetch();
        return;
      }
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: co.key_id, amount: co.amount, currency: "INR", name: "DropPartner",
        description: co.description, order_id: co.order_id,
        handler: async (resp) => {
          try {
            await api.post("/agent/plan/verify", { purchase_id: co.purchase_id, ...resp });
            toast.success("Annual service renewed for 1 year");
            refetch();
          } catch (e) {
            toast.error(errMsg(e));
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cards = [
    { icon: Crown, label: "Plan", value: data.plan.name, testid: "plan-name" },
    { icon: ShoppingBasket, label: "Basket used", value: `${data.basket_used} / ${data.basket_limit}`, testid: "plan-basket" },
    { icon: CalendarClock, label: "Service free until", value: data.service_free_until || "—", testid: "plan-free-until" },
    { icon: ShieldCheck, label: "Renewal status", value: (data.renewal_status || "").replace(/_/g, " "), testid: "plan-renewal-status" },
  ];

  return (
    <div data-testid="my-plan-page">
      <PageHeader title="My Plan" sub="Your subscription, basket limit, service period and invoices." testid="my-plan-title">
        <Button data-testid="renew-plan-btn" onClick={renew} disabled={busy}
          className="bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          Renew service — {fmtINR(data.annual_service_fee)}/yr
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
              <c.icon size={15} className="text-[#FF6B00]" />
            </div>
            <p className="mt-1.5 text-lg font-extrabold capitalize text-[#0F2440]">{c.value}</p>
          </div>
        ))}
      </div>

      {data.renewal_status !== "free_period" && (
        <div data-testid="renewal-alert" className={`mb-6 rounded-xl border px-4 py-3 text-xs ${data.renewal_status === "expired" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
          {data.renewal_status === "expired"
            ? "Your annual service has expired. Renew now to keep your store and marketplace channels running."
            : `Your free service year ended on ${data.service_free_until}. You are in the grace period — renew to avoid interruption.`}
        </div>
      )}

      <div className="mb-6 rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="plan-details">
        <h3 className="mb-3 text-sm font-bold text-[#0F2440]">{data.plan.name} plan includes</h3>
        <ul className="grid gap-1.5 text-xs text-slate-600 sm:grid-cols-2">
          {(data.plan.features || []).map((f) => <li key={f}>· {f}</li>)}
        </ul>
      </div>

      <h3 className="mb-3 text-sm font-bold text-[#0F2440]">Invoices</h3>
      {!data.invoices?.length ? <p className="text-xs text-slate-500">No invoices yet.</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
          <table className="w-full text-left text-xs" data-testid="invoices-table">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-${inv.invoice_no}`} className="border-b border-slate-50">
                  <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{inv.invoice_no}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDate(inv.created_at)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{inv.description}</td>
                  <td className="px-4 py-2.5 font-semibold text-[#0F2440]">{fmtINR(inv.total)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
